import { combineLatest, Observable, BehaviorSubject } from "rxjs";
import { map, startWith } from 'rxjs/operators';
import { addLeadingZeroes } from './_filters';
import { useEffect, useState } from 'react';
import { Transaction } from 'olympe';
import BCP from '../../common/section_bcp/DM_BCP';
import { QA_ROLES } from '../section_sf/Utils';

/**
 * @typedef UserPlantObject
 * @property {string} market
 * @property {string} plantID
 * @property {string} plantDescription
 */

/**
 * Gets the user plants in local storage
 *
 * @return {Array<UserPlantObject>}
 */
export const getUserPlants = () => {
    try {
        const userRole = JSON.parse(localStorage.getItem('plants'));
        const plants = userRole['roles']['roles-details']['company-codes']['plants'];
        return Array.isArray(plants) ? plants : [];
    } catch (e) {
        console.error('getUserPlants', 'error when parsing user role from local storage', e);
        return [];
    }
};

export const getProps = (propsName) => {
    let ColumnObject = {
        [QA_ROLES.CTAG_AG]: BCP.technicalApplicationGroupProp,
        [QA_ROLES.SQA]: BCP.qualityAssuranceProp,
        [QA_ROLES.QA_IN_FACTORY]: BCP.qaInFactoryProp,
        [QA_ROLES.BP]: BCP.businessPartnerProp,
        [QA_ROLES.PM]: BCP.procurementManagerProp,
        [QA_ROLES.PS]: BCP.procurementSpecialistProp,
        [QA_ROLES.REQUESTOR]: BCP.requestorProp,
    };
    return ColumnObject[propsName];
}

export function useObjectProperty($, cloudObject, property, initialValue) {
    const [value, setValue] = useState(cloudObject.get(property) ?? initialValue);
    useEffect(() => {
        const subscription = cloudObject.observe($, property, true).subscribe(setValue);
        return () => { subscription.unsubscribe(); };
    }, []);
    const setter = (newValue) => {
        const transaction = new Transaction();
        transaction.update(cloudObject, property, newValue);
        transaction.execute().catch((e) => console.warn(`failed to update property: ${e}`));
    };
    return [value, setter];
}

/**
 * Combine props together in an object, combine also the current language
 *
 * @param {![{name: string, prop: Property, defaultValue: *}]} props
 */
export function combineProps(props) {
    const names = props.map(prop => prop.name);
    const sources = props.map(prop => {
        let obs = prop.prop.observe();
        // TODO remove defaultValue once bug with number defaultValue is fixed
        if (prop.defaultValue !== undefined) {
            obs = obs.pipe(startWith(prop.defaultValue));
        }
        return obs;
    });
    return combineLatest(sources).pipe(map(values => {
        let i = 0;
        return values.reduce((acc, cur) => ({ ...acc, [names[i++]]: cur }), {});
    }));
}
/**
 * React hook to update a value based on a delay (debounced)
 *
 * @param {string} value
 * @param {number} delay - in milliseconds
 *
 * @return {string}
 */
export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);
    return debouncedValue;
};

/**
 * Create events usable by React component
 *
 * @param {![{name: string, event: Event, before: function(*, *)}]} events
 */
export function createEvents(events) {
    const setControlFlow = event => event.set(Date.now());
    return events.reduce((acc, cur) => ({ ...acc, [cur.name]: (a, v) => { if (cur.before) cur.before(a, v); setControlFlow(cur.event); } }), {});
}

/**
 * Get an observable that retains its value (with method getValue() ) from an observable
 *
 * From https://stackoverflow.com/questions/46207592/rxjs-5-converting-an-observable-to-a-behaviorsubject
 *
 * @param {Observable} observable
 * @param {*} initValue
 * @return {{subject: BehaviorSubject, stopWatching: (function(): *)}}
 */
export function convertObservableToBehaviorSubject(observable, initValue) {
    const subject = new BehaviorSubject(initValue);
    const subscription = observable.subscribe(subject);
    return {
        subject,
        stopWatching: () => subscription.unsubscribe()
    };
}

/**
 * Returns a JSON as an Object, with a default value
 *
 * @param {string} json
 * @param {Object} defaultValue
 */
export function getObjectFromJSON(json, defaultValue = {}) {
    let style = defaultValue;
    try {
        style = JSON.parse(json);
    } catch (e) { }
    return style ? style : defaultValue;
}
/** This will enable to sleep for mentioned seconds*/
export function sleep(ms) {
    return new Promise((res) => setTimeout(res, ms));
}

/**
 * Returns a JSON as an Object, with a default value
 *
 * @param {string} selection
 */
export function updateSelectedArray(selection, type) {
    if (selection && selection.length !== 0 && selection !== 'true' && selection !== 'false') {
        if (type === 'materials') {
            try {
                return JSON.parse(selection).map(
                    materialsWithDescription => addLeadingZeroes(materialsWithDescription.split(' - ')[0])
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'plant') { // section_sku Implementation
            try {
                return JSON.parse(selection).map(
                    item => item.split(' - ')[1]
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'plants') { // R&C implementation
            try {
                return JSON.parse(selection).map(
                    item => item.split(' - ')[0]
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'modifiedBy') {
            try {
                return JSON.parse(selection).map(
                    user => user.split('(').pop().split(')')[0])
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'specifications' || type === 'reasonCode') {
            try {
                return JSON.parse(selection).map(
                    item => item.split(' - ')[0]
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'nestecProductCategory') {
            try {
                return JSON.parse(selection).map(
                    item => {
                        if (item.split(' - ')[0].length === 1) return "0" + item.split(' - ')[0]
                        else return item.split(' - ')[0]
                    }
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
        if (type === 'mainVendor' || type === 'vendorSiteNumber') {
            try {
                return JSON.parse(selection).map(
                    item => {
                        let splitItem = `${'0' + item.split(' - ')[0]}`
                        return splitItem
                    }
                )
            } catch (e) {
                console.error(`The ${selection} property is either empty, or not JSON parsable. + ${e}`)
            }
        }
    }
    return JSON.parse(selection)
}
